import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";

export default function StoreSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data: d } = await api.get(`/store/status/${sessionId}`);
        setData(d);
        if (d.payment_status === "paid") { setStatus("paid"); return; }
      } catch (_) {
        if (attempts >= 8) { setStatus("error"); return; }
      }
      if (attempts < 8) setTimeout(poll, 1800); else setStatus("timeout");
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect; referenced values are stable module imports/constants
  }, [sessionId]);

  return (
    <div data-testid="store-success-page" className="bg-white text-[#0a0a0a] min-h-screen flex flex-col">
      <Seo title="Order confirmation — Universtrend" description="Your order is being confirmed." />
      <Header />
      <main className="flex-1 flex items-center">
        <div className="mx-auto max-w-[900px] w-full px-6 lg:px-10 py-16 text-center">
          {status === "polling" && (
            <>
              <div className="overline text-[#52525b] mb-3">Confirming Stripe…</div>
              <h1 className="font-display font-extrabold text-5xl lg:text-7xl tracking-tighter">Hang tight.</h1>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="overline text-[#ff3b30] mb-3">Paid ✓</div>
              <h1 className="font-display font-extrabold text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
                Thank you.<br />
                <span className="text-[#ff3b30]">{data?.label}</span>
              </h1>
              <p className="mt-6 text-[#52525b] max-w-xl mx-auto">
                {data?.kind === "report" && "Your PDF will be emailed within 24 hours."}
                {data?.kind === "article" && "Our editorial team will contact you within 24h to co-write your article."}
                {data?.kind === "subscription" && "Your Pro newsletter access is active. First issue lands Monday 8am."}
                {data?.kind === "job" && "Your job posting is live now on /clips."}
              </p>
              <p className="mt-3 font-mono text-xs text-[#52525b]">Receipt sent to <b>{data?.email}</b></p>
              <Link to="/" className="hard-shadow inline-block mt-10 border border-black bg-black text-white px-6 py-3 overline">
                Back to the Atlas →
              </Link>
            </>
          )}
          {(status === "error" || status === "timeout") && (
            <>
              <div className="overline text-[#52525b] mb-3">Hmm</div>
              <h1 className="font-display font-extrabold text-4xl lg:text-6xl tracking-tighter">Couldn&apos;t confirm.</h1>
              <p className="mt-4 text-sm text-[#52525b]">If you were charged, we&apos;ll email you. Otherwise try again.</p>
              <Link to="/store" className="overline inline-block mt-8 border border-black px-5 py-2.5 hover:bg-black hover:text-white">← Store</Link>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
